import { UserRole } from "@features/settings/types/settings.types";
import { Button } from "@shared/components/ui/Button";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import AuthenticatedLayout from "@shared/layouts/AuthenticatedLayout";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ResourcePagination } from "../components/ResourcePagination";
import { useResourcesStore } from "../store/useResourcesStore";
import {
  Resource,
  ResourceApprovalStatus,
  ReviewResourceData,
} from "../types/resource.types";

const STATUS_LABELS: Record<ResourceApprovalStatus, string> = {
  [ResourceApprovalStatus.PENDING]: "Pending",
  [ResourceApprovalStatus.APPROVED]: "Approved",
  [ResourceApprovalStatus.REJECTED]: "Rejected",
};

const STATUS_CLASSES: Record<ResourceApprovalStatus, string> = {
  [ResourceApprovalStatus.PENDING]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  [ResourceApprovalStatus.APPROVED]:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  [ResourceApprovalStatus.REJECTED]:
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const ResourceManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useProfile();
  const {
    adminResources,
    totalAdminResources,
    adminCurrentPage,
    isLoadingAdmin,
    adminError,
    fetchAdminResources,
    reviewResource,
  } = useResourcesStore();

  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewStatus, setReviewStatus] =
    useState<ResourceApprovalStatus | null>(null);
  const [reviewComment, setReviewComment] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const limit = 12;
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchAdminResources(1, limit);
    }
  }, [fetchAdminResources]);

  useEffect(() => {
    if (user && user.role !== UserRole.ADMIN) {
      navigate("/resources");
    }
  }, [user, navigate]);

  const handleOpenReview = (resource: Resource) => {
    setSelectedResource(resource);
    setReviewStatus(
      resource.approvalStatus !== ResourceApprovalStatus.PENDING
        ? resource.approvalStatus
        : null,
    );
    setReviewComment(resource.reviewComment || "");
    setShowReviewModal(true);
  };

  const handleSaveReview = async () => {
    if (!selectedResource || !reviewStatus) {
      toast.error("Please select Approve or Reject before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const data: ReviewResourceData = {
        status: reviewStatus as
          | ResourceApprovalStatus.APPROVED
          | ResourceApprovalStatus.REJECTED,
        comment: reviewComment || undefined,
      };
      const updated = await reviewResource(
        selectedResource.id!,
        data,
        user?.profile ?? undefined,
      );
      if (updated) {
        toast.success("Review saved successfully.");
        setShowReviewModal(false);
      } else {
        toast.error("Failed to save review.");
      }
    } catch {
      toast.error("An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (ts: any): string => {
    if (!ts) return "—";
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = Math.ceil(totalAdminResources / limit);

  const featuredImageUrl = (resource: Resource): string | undefined => {
    if (!resource.featuredImage?.length) return undefined;
    const variants = resource.featuredImage[0].variants;
    const webVariant = variants?.find((v: any) => v.type === "web");
    return webVariant?.url || variants?.[0]?.url;
  };

  const isReviewed = (resource: Resource) =>
    resource.approvalStatus === ResourceApprovalStatus.APPROVED ||
    resource.approvalStatus === ResourceApprovalStatus.REJECTED;

  return (
    <AuthenticatedLayout
      pageHeader={{
        title: "Resource Management",
        actions: [
          {
            label: "View All Resources",
            onClick: () => navigate("/resources"),
            variant: "primary",
          },
        ],
        search: { show: false },
      }}
      isLoading={isLoadingAdmin}
      error={adminError ? new Error(adminError) : null}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-4 w-full max-w-9xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          The table below lists all resource approval requests submitted by
          members. Review each submission and approve or reject it to control
          what gets published.
        </p>

        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 whitespace-nowrap">Created</th>
                  <th className="px-4 py-3 max-w-[200px]">Title</th>
                  <th className="px-4 py-3 whitespace-nowrap">Creator</th>
                  <th className="px-4 py-3 whitespace-nowrap">Organization</th>
                  <th className="px-4 py-3 whitespace-nowrap">Reviewed By</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action Date</th>
                  <th className="px-4 py-3 max-w-[180px]">Comments</th>
                  <th className="px-4 py-3 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {adminResources.length === 0 && !isLoadingAdmin && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-10 text-center text-gray-400 dark:text-gray-500"
                    >
                      No resource requests found.
                    </td>
                  </tr>
                )}
                {adminResources.map((resource) => (
                  <tr
                    key={resource.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">
                      {formatDate(resource.createdAt)}
                    </td>
                    <td className="px-4 py-3 max-w-[200px]">
                      <span
                        className="block truncate text-gray-800 dark:text-gray-200 font-medium"
                        title={resource.title}
                      >
                        {resource.title}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <img
                          src={getProfileImage(resource.author) || undefined}
                          alt={resource.author?.name || "User"}
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                        />
                        <span className="text-gray-800 dark:text-gray-200 text-sm">
                          {resource.author?.name || "Unknown"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">
                      {resource.author?.organization?.name || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {resource.reviewedByProfile ? (
                        <div className="flex items-center gap-2">
                          <img
                            src={
                              getProfileImage(resource.reviewedByProfile) ||
                              undefined
                            }
                            alt={resource.reviewedByProfile?.name || "Admin"}
                            className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                          />
                          <span className="text-gray-800 dark:text-gray-200 text-sm">
                            {resource.reviewedByProfile?.name || "—"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">
                      {formatDate(resource.reviewedAt)}
                    </td>
                    <td className="px-4 py-3 max-w-[180px]">
                      {resource.reviewComment ? (
                        <span
                          className="block truncate text-gray-600 dark:text-gray-300 text-sm"
                          title={resource.reviewComment}
                        >
                          {resource.reviewComment}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          —
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[resource.approvalStatus] || STATUS_CLASSES[ResourceApprovalStatus.PENDING]}`}
                      >
                        {STATUS_LABELS[resource.approvalStatus] || "Pending"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenReview(resource)}
                      >
                        View Resource
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <ResourcePagination
              currentPage={adminCurrentPage}
              totalPages={totalPages}
              onPageChange={(page) => fetchAdminResources(page, limit)}
              limit={limit}
              onLimitChange={() => {}}
            />
          )}
        </div>
      </div>

      {/* Review Modal — full-screen overlay with wide panel */}
      {showReviewModal && selectedResource && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 transition-opacity"
            aria-hidden="true"
          />

          {/* Dialog */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 sm:px-6"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
              {/* Close button */}
              <button
                onClick={() => setShowReviewModal(false)}
                className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Hero image — full width, no padding */}
              {featuredImageUrl(selectedResource) ? (
                <img
                  src={featuredImageUrl(selectedResource)}
                  alt={selectedResource.title}
                  className="w-full h-64 object-cover rounded-t-lg"
                />
              ) : (
                <div className="w-full h-24 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-700 rounded-t-lg" />
              )}

              <div className="p-6">
                {/* Status badge */}
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[selectedResource.approvalStatus] || STATUS_CLASSES[ResourceApprovalStatus.PENDING]}`}
                  >
                    {STATUS_LABELS[selectedResource.approvalStatus] ||
                      "Pending"}
                  </span>
                </div>

                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {selectedResource.title}
                </h2>

                <div className="flex items-center gap-2 mb-5 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
                  <img
                    src={getProfileImage(selectedResource.author) || undefined}
                    alt={selectedResource.author?.name || "User"}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span>{selectedResource.author?.name || "Unknown"}</span>
                  {selectedResource.author?.organization?.name && (
                    <>
                      <span>·</span>
                      <span>{selectedResource.author.organization.name}</span>
                    </>
                  )}
                  <span>·</span>
                  <span>{formatDate(selectedResource.createdAt)}</span>
                </div>

                <div
                  className="text-sm text-gray-700 dark:text-gray-200 mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: selectedResource.description || "",
                  }}
                />

                {/* Review section */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                  {isReviewed(selectedResource) ? (
                    /* Already reviewed — read-only */
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        This resource has already been reviewed and cannot be
                        modified.
                      </p>
                      {selectedResource.reviewComment && (
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                            Review Comment
                          </p>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            {selectedResource.reviewComment}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button
                          variant="secondary"
                          onClick={() => setShowReviewModal(false)}
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Pending — editable */
                    <div>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                        Select a decision:
                      </p>
                      <div className="flex gap-4 mb-6">
                        {/* Approve */}
                        <Button
                          variant="approve"
                          size="md"
                          isActive={
                            reviewStatus === ResourceApprovalStatus.APPROVED
                          }
                          onClick={() =>
                            setReviewStatus(
                              reviewStatus === ResourceApprovalStatus.APPROVED
                                ? null
                                : ResourceApprovalStatus.APPROVED,
                            )
                          }
                          fullWidth
                          leftIcon={
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          }
                        >
                          Approve
                        </Button>

                        {/* Reject */}
                        <Button
                          variant="reject"
                          size="md"
                          isActive={
                            reviewStatus === ResourceApprovalStatus.REJECTED
                          }
                          onClick={() =>
                            setReviewStatus(
                              reviewStatus === ResourceApprovalStatus.REJECTED
                                ? null
                                : ResourceApprovalStatus.REJECTED,
                            )
                          }
                          fullWidth
                          leftIcon={
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          }
                        >
                          Reject
                        </Button>
                      </div>

                      <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          Comments{" "}
                          <span className="text-gray-400 font-normal">
                            (optional)
                          </span>
                        </label>
                        <textarea
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          rows={3}
                          placeholder="Add your review comments..."
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                      </div>

                      <div className="flex gap-3 justify-end">
                        <Button
                          variant="secondary"
                          onClick={() => setShowReviewModal(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleSaveReview}
                          disabled={isSaving || !reviewStatus}
                          isLoading={isSaving}
                          loadingText="Saving..."
                        >
                          Save Review
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Animation keyframes */}
          <style>{`
            @keyframes floatBounce {
              0%, 100% { transform: translateY(0) scale(1.02); }
              50% { transform: translateY(-4px) scale(1.04); }
            }
          `}</style>
        </>
      )}
    </AuthenticatedLayout>
  );
};

export default ResourceManagementPage;
